/* eslint-disable max-len */
import { auctionCurrentPrice, toReadable, consoleLog, sixDigits } from "../../utils/methods";
import { AppRoutes, landDetailLink } from "../../config";
import { IconButton, Button, ButtonProps, Grid, styled, IconButtonProps, Container, Box, useMediaQuery, useTheme } from "@mui/material";
import { CURRENCY_NAME, DATE_TIME_FORMAT, TIME_DISPLAY_UNIT } from "../../utils/constants";
import moment from "moment";
import { CountdownTimer, AddressLink, CurrencyLabel, PulseCircle, Text } from "../";
import { Link, LinkProps } from "react-router-dom";
import { MapLink } from "../../pages/Home/Home";
import { LandToken, TokenMeta } from "../../types/LandToken";
import { useContext, useEffect, useMemo, useState } from "react";
import { formatValue } from "../../utils/formatValue";
import { AppContext } from "../../contexts";
// import LikeIcon from "../../assets/like_icon.svg";
import ShareIcon from "../../assets/share_icon.svg";
import ShareModal from "../SharePopup/index";
import { getReadOnlyContract } from "../../services/contract.service";
import ArrowForwardSharpIcon from '@mui/icons-material/ArrowForwardSharp';
import StatusCycle from "../StatusCycle";
import { landStatusStates } from "../../utils/landMeta";

type FeaturedLandProps = {
  land: Record<"meta", Required<TokenMeta>> & LandToken;
  blogStory?: boolean;
};

const FeaturedLand = ({
  land,
  blogStory = false
}: FeaturedLandProps) => {
  const appContext = useContext(AppContext);
  const [isSold, setSold] = useState<boolean>(false);
  const [treesDepleted, setTreesDepleted] = useState<boolean>(false);
  const [isLiveAuction, setLiveAuction] = useState<boolean>(false);
  const [hasBids, setHasBids] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<Date | boolean>(false);
  const [mintRate, setMintRate] = useState<string | null>(null);
  const [remainingTrees, setRemainingTrees] = useState<string | null>(null);
  const [treesValue, setTreesValue] = useState<string | null>(null);
  const [patron, setPatron] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
  const [fibonacciNumber, setFibonacciNumber] = useState<number | null>(null);
  const [isOffer, setIsOffer] = useState<boolean>(false);

  //org popup

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));



  useEffect(() => {
    const contractDuration = land.meta?.contractPeriod;
    const isOffer = land.offerStatus === "opened";
    console.log("isOffer", isOffer);
    setIsOffer(isOffer);
    const isSold = land.etNft?.status === "sold";
    const endDate = land.etNft?.etAuction?.auctionStart
      ? moment(land.etNft?.etAuction.auctionStart).add(land.etNft.etAuction.duration, "milliseconds").toDate()
      : land.etNft?.etAuction?.auctionEnd
        ? moment(land.etNft?.etAuction.auctionEnd).toDate()
        : false;

    // endingPrice is "0" until the first bid, then the highest one (auctionCurrentPrice).
    if (Number(land.etNft?.etAuction?.endingPrice) > 0) {
      setHasBids(true);
    }

    if (isSold) {
      setPatron(land.etNft?.ownerAddress || null);
    }

    const isExpired =
      isSold && moment(land.etNft?.easementActivated?.contractStart).add(contractDuration, TIME_DISPLAY_UNIT).isBefore();
    const remTrees = formatValue(land.etNft?.easementActivated?.treeTotal as any, false) - (formatValue(land.etNft?.easementActivated?.rewardClaimed as any, false) || 0);
    const isTreesDepleted = isSold && isExpired && remTrees <= 0;
    const isLiveAuction = land.etNft?.etAuction?.status === "started";
    setLiveAuction(isLiveAuction);
    const rate = getMintRate();
    setMintRate(rate);
    setTreesDepleted(isTreesDepleted);
    setSold(isSold);
    setEndDate(endDate);
    getFibonacciNumber(land.etNft?.id as any);
  }, [land]);

  const getFibonacciNumber = async (n: number) => {

    try {
      const ecofuturesCoreContract = getReadOnlyContract("ecofutures-core");
      const tokenId = n;
      if (ecofuturesCoreContract) {
        const easementData = await ecofuturesCoreContract.getEasement(tokenId);


        // Convert the last element (BigInt) to a regular number
        if (easementData && easementData.length > 0) {
          const lastElement = easementData[easementData.length - 2];
          const fibonacciNumber = Number(lastElement);
          setFibonacciNumber(fibonacciNumber);
        }
      }
      consoleLog({ fibonacciNumber });
    } catch (error) {
      console.log({ error });
    }
  };

  const getMintRate = () => {
    const total = formatValue(land.etNft?.easementActivated?.treeTotal as any, false);
    const claimed = formatValue(land.etNft?.easementActivated?.rewardClaimed as any, false);
    const remaining = total - claimed > 0 ? total - claimed : 0;
    const rate = appContext?.mintRate;
    if (rate) {
      const balance = formatValue(remaining * rate, true, 2);
      const valueText = `(@ $${formatValue(rate, true, 4)} / TREE)`;
      setRemainingTrees(String(sixDigits(balance)));
      setTreesValue(valueText);
    } else {
      setRemainingTrees(formatValue(remaining, true, 2));
    }

    if (total - claimed <= 0) {
      return null;
    }
    const totalTrees = formatValue(land.etNft?.totalTreeForEasement as any, false);
    const days = moment.duration(land.meta?.contractPeriod, TIME_DISPLAY_UNIT).as("days");
    return formatValue(totalTrees / (days - 1), true, 3);

  };

  // Same rotating location line as the LandPage hero: place, lon/lat, size, elevation.
  const statusStates = useMemo(() => landStatusStates(land.meta), [land.meta]);

  const url = landDetailLink(land.id);

  const handleShare = () => {
    setShareModalOpen(true);
  };

  return (
    <>
      <Grid
        width="100%"
        flexDirection="column"
        display="flex"
        sx={{ borderLeft: 0, borderRight: 0, borderColor: "border.dark", backgroundColor: "transparent" }}
      >
        <HighlightedLand>
          <LandImage src={land.content}
            sx={{
              borderRadius: { xs: "20px 20px 0px 0px", md: "20px 0px 0px 20px" },
              minHeight: { xs: "auto", md: "600px" },
              maxHeight: { xs: "auto", md: "800px" }
            }} />
          <LandInfo sx={{ p: { xs: 9, sm: 12 }, borderRadius: { xs: "0px 0px 20px 20px", md: "0px 20px 20px 0px" }, backgroundColor: "back.featureLandCard" }}>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
              <LandInfoBlock sx={{ p: 0, border: 0, }}>
                <Text
                  medium
                  sx={{
                    color: "black",
                    fontFamily:
                      "'Inter', -apple-system, BlinkMacSystemFont, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
                  }}>
                  #{land.etNft?.id}
                  {/* {fibonacciNumber ? `/${fibonacciNumber}` : ""} */}
                </Text>
                <Text large sx={{ fontWeight: 700 }} >
                  {land.meta?.name}
                </Text>
                {statusStates.length > 0 && (
                  // Status line (StatusCycle): shimmers while a state holds, then swaps to the
                  // next. The base colour is the link's own; the card is cream in both themes,
                  // so the band darkens it.
                  <Text medium sx={{ fontWeight: 400, letterSpacing: "0.08em", "--think-highlight": "#0d0d0d" }} >
                    <MapLink to={AppRoutes.Explore.path + "?id=" + land.id} style={{
                      fontFamily:
                        "'Inter', -apple-system, BlinkMacSystemFont, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
                    }}>
                      <StatusCycle states={statusStates} />
                    </MapLink>
                  </Text>
                )}
              </LandInfoBlock>
              {!blogStory && (

                <ResponsiveContainer>
                  <ShareButton onClick={handleShare} to={""}>
                    <img src={ShareIcon} alt="share" />
                  </ShareButton>
                </ResponsiveContainer>

              )}
            </Box>
            <LandDetailsBlock>
              <LandInfoBlock sx={{ borderTop: "1px solid #ccc" }}>
                <LeftTextBox $blogStory={blogStory}>

                  <Text medium sx={{ color: "#999", mb: "5px" }}>Status</Text>
                </LeftTextBox>
                <LeftTextBox $blogStory={blogStory}>
                  <Text
                    medium
                    bold
                    numeric
                    contrast
                    capital
                    sx={{
                      color: isSold ? "action.pulse" : isLiveAuction ? "action.pulse" : "primary.main",
                      alignItems: "center",
                      fontFamily: "'IBM Plex Mono', monospace"
                    }}
                  >
                    {(isLiveAuction || isSold) && (
                      <PulseCircle hide={treesDepleted} sold={isSold} />
                    )}
                    {isSold ? (treesDepleted ? "Expired" : isOffer ? "Live Offer" : "Active") : (isLiveAuction ? (isSmallScreen ? "Live" : "Live Auction") : "Verified")}
                  </Text>
                </LeftTextBox>
              </LandInfoBlock>

              <LandInfoBlock sx={{ direction: "rtl", borderTop: "1px solid #ccc" }}>
                <RightTextBox $blogStory={blogStory}>
                  <Text medium sx={{ color: "#999", mb: "5px" }}>Eco Score</Text>
                </RightTextBox>
                <RightTextBox $blogStory={blogStory}>
                  <Text medium numeric contrast capital bold sx={{ fontFamily: "'IBM Plex Mono', monospace", }} >
                    {(land.ecoScore * 100).toFixed()}
                  </Text>
                </RightTextBox>
              </LandInfoBlock>

              <LandInfoBlock >
                <LeftTextBox $blogStory={blogStory}>
                  <Text medium sx={{ color: "#999", mb: "5px" }}>Trustee</Text>
                </LeftTextBox>
                {/* <button onClick={() => setOrgOpen(true)} style={{ border: "none", background: "none", padding: 0, margin: 0, cursor: "pointer" }}> */}
                <LeftTextBox $blogStory={blogStory}>
                  <Text medium underline numeric bold sx={{ width: "100%", fontFamily: "'IBM Plex Mono', monospace", textAlign: "start" }}>
                    <AddressLink
                      sx={{
                        width: "100%",
                        "& a": {
                          fontWeight: "bold",
                          fontSize: "medium",
                          fontFamily: "'IBM Plex Mono', monospace",
                        }
                      }}
                      type="user"
                      name={land.trust?.organization?.name}
                      address={land.trust?.address as string}

                    />
                    {/* {land.trust?.organization?.name} */}

                  </Text>
                </LeftTextBox>
                {/* </button> */}
              </LandInfoBlock>

              <LandInfoBlock sx={{ direction: "rtl" }}>
                <RightTextBox $blogStory={blogStory}>
                  <Text medium sx={{ color: "#999", mb: "5px" }}>

                    Guardian

                  </Text>
                </RightTextBox>
                <RightTextBox $blogStory={blogStory}>
                  <Text medium numeric>
                    <AddressLink type="user" address={land.minterAddress}
                      sx={{
                        width: "100%",
                        "& a": {
                          fontWeight: "bold",
                          fontSize: "medium",
                          fontFamily: "'IBM Plex Mono', monospace",
                        }
                      }} />
                  </Text>
                </RightTextBox>
              </LandInfoBlock>
              {(land?.etNft?.etAuction?.status || land?.etNft?.etAuction?.status === "ended") && (
                <>
                  <LandInfoBlock>

                    <Grid container flexDirection="column" item width="auto">
                      <LeftTextBox $blogStory={blogStory}>
                        <Text medium sx={{ color: "#999", mb: "5px" }}>
                          {isSold ? "Sold for" : hasBids ? "Current bid" : "Opening bid"}
                        </Text>
                      </LeftTextBox>
                      <LeftTextBox $blogStory={blogStory}>
                        <Text contrast medium numeric bold capital sx={{ gap: 3, fontFamily: "'IBM Plex Mono', monospace" }}>
                          {toReadable(auctionCurrentPrice(land.etNft?.etAuction) || "0")} <CurrencyLabel sx={{ fontWeight: "600" }} currency={CURRENCY_NAME} />
                        </Text>
                      </LeftTextBox>
                      {patron && (
                        <LeftTextBox $blogStory={blogStory}>
                          <Text medium sx={{
                            gap: 3,
                            color: "#999",
                            "& a": {
                              color: "#999 !important",
                            }
                          }}>
                            <ArrowForwardSharpIcon sx={{ fontSize: "10px", color: "#999" }} />
                            <AddressLink address={patron} type="user"

                              sx={{

                                width: "100%",
                                "& a": {
                                  color: "#999 !important",
                                  fontWeight: "bold",
                                  fontSize: { xs: "14px", sm: "14px" },
                                  fontFamily: "'IBM Plex Mono', monospace",
                                },
                                "& a.userAddress": {
                                  color: "#999 !important",
                                },
                                "& a.userAddress:hover": {
                                  color: "#999 !important",
                                }
                              }} />
                          </Text>
                        </LeftTextBox>
                      )}
                    </Grid>
                  </LandInfoBlock>

                  <LandInfoBlock sx={{ direction: "rtl" }}>
                    <Grid container flexDirection="column" item width="auto">
                      {patron ? (<div style={{ direction: "rtl" }}>
                        <RightTextBox $blogStory={blogStory}>
                          <Text medium sx={{
                            color: "#999",
                            mb: "5px",
                            justifyContent: "end",
                            minWidth: "120px",
                            textAlign: "right"
                          }}>
                            Value
                          </Text>
                        </RightTextBox>
                      </div>
                      ) : (
                        <RightTextBox $blogStory={blogStory} style={{}}>
                          <Text medium sx={{ color: "#999", mb: "5px", justifyContent: "end" }}>
                            Auction {isSold ? "ended at" : "ends in"}
                          </Text>
                        </RightTextBox>
                      )}
                      {patron ? (
                        <div style={{ direction: "rtl" }}>

                          <RightTextBox $blogStory={blogStory}>
                            <Text numeric contrast medium sx={{ flexDirection: "column", direction: 'ltr' }}>
                              <Text numeric medium bold sx={{ width: "100%", direction: 'ltr' }}>

                                {remainingTrees} <CurrencyLabel sx={{ color: "black" }} currency={CURRENCY_NAME} />
                              </Text>
                              <Text numeric bold sx={{
                                display: "flex",
                                direction: 'ltr',
                                color: "#999",
                                fontSize: { xs: "14px", sm: "14px" },
                              }}>

                                {treesValue}
                              </Text>
                            </Text>
                          </RightTextBox>
                        </div>
                      ) : (
                        <div style={{ direction: "rtl" }}>
                          <RightTextBox $blogStory={blogStory}>
                            <Text medium contrast numeric style={{ direction: 'ltr' }}>
                              {endDate ? (
                                isSold ? (
                                  moment(endDate as Date).format(DATE_TIME_FORMAT)
                                ) : (
                                  <CountdownTimer sx={{ width: "100%", fontWeight: '700', fontSize: { xs: "12px", sm: "14px" }, }} medium targetDate={endDate as Date} />
                                )
                              ) : null}
                            </Text>
                          </RightTextBox>
                        </div>
                      )}
                    </Grid>
                  </LandInfoBlock>
                </>)}
            </LandDetailsBlock>

            {mintRate && (
              <LandInfoBlock sx={{ display: "none" }}>
                <Text medium sx={{ color: "#999", mb: "5px" }}>
                  Mint per day
                </Text>
                <Text medium numeric contrast capital>
                  {mintRate}
                  <CurrencyLabel />
                </Text>
              </LandInfoBlock>
            )}

            <Grid item width={"100%"}>
              <ViewButton LinkComponent={Link} to={url}>
                View EFT
              </ViewButton>
            </Grid>


          </LandInfo>
        </HighlightedLand >
        <ShareModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          shareUrl={`${window.location.origin}${landDetailLink(land.id)}`}
          shareText={`Check out this land on EFT: ${land.meta?.name}`}
        />
      </Grid >

    </>
  );
};

const ResponsiveContainer = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  @media (min-width: 992px) {
    width:200px
  }

  @media (max-width: 600px) {
    justify-content: center;
    flex-direction: row;
    width: 50%;
    align-items: center;
  }
`;

const ShareButton = styled(IconButton) <IconButtonProps & LinkProps>`
  // background-color: ${({ theme }) => theme.palette.border.default};
  font-weight: 400;
  margin-top: 24px;
  @media (max-width: 1024px) {
    margin-top: 0;
  }
  @media (max-width: 992px) {
    
    font-size: 14px;
  }
  @media (max-width: 560px) {
    width: 100%;
  }
`;

// const LikeButton = styled(IconButton) <IconButtonProps & LinkProps>`
//   font-weight: 400;
//   margin-top: 24px;
//   @media (max-width: 1024px) {
//     margin-top: 0;
//   }
//   @media (max-width: 992px) {
//     font-size: 14px;
//   }
//   @media (max-width: 560px) {
//     max-width: 100%;
//   }
// `;

export const RightTextBox = styled("div") <{ $blogStory?: boolean }>`
  display: flex;
  width: ${({ $blogStory }) => ($blogStory ? '100%' : '55%')};
  justify-content: end;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

export const LeftTextBox = styled("div") <{ $blogStory?: boolean }>`
  display: flex;
  width: ${({ $blogStory }) => ($blogStory ? '100%' : '55%')};
  justify-content: start;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const ViewButton = styled(Button) <ButtonProps & LinkProps>`
  font-weight: 400;
  margin-top: 24px;
  width: 100%;
  height: 60px;
  border-radius: 24px;
  
  &:hover {
    border: none;
  }
  
  @media (max-width: 1024px) {
    margin-top: 0;
  }
  @media (max-width: 992px) {
  width: 100%;
    height: 36px;
    font-size: 14px;
  }
  @media (max-width: 560px) {
    max-width: 100%;
    width: 100%;
  }
`;

const HighlightedLand = styled("div")`
  display: grid;
  grid-template-columns: 50% 50%;
  justify-content: center;

  @media (max-width: 560px) {
    display: flex;
    flex-direction: column;
  }
`;

const LandImage = styled("img")`
  width: 100%;
  display: block;
  object-fit: cover;
  margin: auto;

  @media (max-width: 560px) {
    // width: auto;
    // max-width: min(calc(100% - 24px), 360px);
    // margin: 12px auto 0;
    // border: 1px solid ${({ theme }) => theme.palette.border.dark};
     height: 320px;
  }
`;

const LandInfoBlock = styled("div")`
  padding: 24px 0;
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.default};
  align-items: end;

  // &:nth-last-of-type(2),
  // &:last-of-type {
  //   border-bottom: none;
  // }

  @media (max-width: 850px) {
    padding: 12px 0;
  }

  &:nth-of-type(odd) {
    padding-right: 6px;
  }

  & > span:first-of-type {
    line-height: 1.4;
    color: ${({ theme }) => theme.palette.text.disabled};
  }
  & > span:last-of-type {
    margin-top: 3px;
  }
`;

const LandDetailsBlock = styled("div")`
  display: grid;
  grid-template-columns: 50% 50%;
  margin-bottom: 12px;
`;

const LandInfo = styled("div")`
  background-color: ${({ theme }) => theme.palette.background.default};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;

  @media (max-width: 1024px) {
    justify-content: space-between;
  }

  @media (max-width: 560px) {
    padding: 18px !important;
  }
`;

export default FeaturedLand;
